import { useEffect, useMemo, useState } from 'react';

import type {
  ColumnInference,
  ColumnPolicyOverride,
  RasSchemaResponse,
} from '../services/types';

import { Button } from './Button';
import { JsonEditor } from './JsonEditor';

export interface SchemaReviewProps {
  schema: RasSchemaResponse;
  /** Draft overlay (unsaved edits). If present, editor shows this instead of `schema`. */
  draft?: RasSchemaResponse;
  /** Called when the reviewer edits the underlying json_schema via the editor. */
  onDraftChange?: (next: RasSchemaResponse) => void;
  /** Called when the reviewer clicks "Save draft" — parent decides where to persist. */
  onSave?: (next: RasSchemaResponse) => Promise<void> | void;
  /** Called when the reviewer clicks "Discard draft". */
  onDiscard?: () => void;
  /**
   * v0.5 Stage E.4 — initial column-policy overrides currently in
   * force on this run. Renders the per-column threshold inputs with
   * pre-filled values; absent / empty → reviewer sees the cleanser's
   * default placeholder. The parent (RunDetailPage) hydrates this
   * from the run summary.
   */
  initialColumnPolicies?: Record<string, ColumnPolicyOverride>;
  /**
   * v0.5 Stage E.4 — handler for the per-column threshold save flow.
   * Called with a map of every override the reviewer changed in this
   * session (added, edited, or cleared). Empty map = nothing to
   * persist; the parent should still re-kick if it wants the
   * existing overrides to take effect on this run.
   *
   * Receives ``invalid_threshold: null`` for cleared overrides so the
   * parent can dispatch the cleared-event audit through the PATCH
   * endpoint.
   */
  onSaveColumnPolicies?: (
    overrides: Record<string, ColumnPolicyOverride>,
  ) => Promise<void> | void;
}

/**
 * Reviewer view of the RAS schema.
 *
 * Shows at a glance:
 *   - the plain-English summary the agent produced
 *   - PII and ambiguous-column chips (links/anchors to editor rows would be
 *     nice-to-have in Stage 3)
 *   - the full JSON schema, editable with AJV validation against the
 *     JSON-Schema meta-schema
 *
 * Drafts live in the parent (usually the Zustand store) so the reviewer can
 * walk away from the tab and come back without losing edits.
 */
export function SchemaReview({
  schema,
  draft,
  onDraftChange,
  onSave,
  onDiscard,
  initialColumnPolicies,
  onSaveColumnPolicies,
}: SchemaReviewProps) {
  const active = draft ?? schema;
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setSaveError(null);
  }, [draft]);

  const save = async () => {
    if (!onSave || !draft) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(draft);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const exportSchema = () => {
    // The UI/UX dev plan called out "export/download actions require
    // explicit confirmation modal" as a Stage 4 security guardrail.
    // Schema contains structural metadata only — no PII values — so
    // the risk is low, but the confirm flow establishes the pattern
    // that any future download (e.g. cleaned CSVs in v0.2) must
    // follow. v0.2 should upgrade both this and the PII-reveal confirm
    // to a proper modal component with audit logging.
    const confirmed = window.confirm(
      'Export this schema as a JSON file? The file contains column names, inferred types, and metadata but no row-level data. This action is recorded in the run audit trail in v0.2.',
    );
    if (!confirmed) return;

    const filename = `${active.schema_version || 'schema'}.json`.replace(
      /[^A-Za-z0-9._-]+/g,
      '_',
    );
    const blob = new Blob([JSON.stringify(active, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <section aria-labelledby="schema-title" className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <h2 id="schema-title" className="m-0">
            Schema review
          </h2>
          <code className="text-xs text-muted">{active.schema_version}</code>
          {draft && (
            <span className="text-xs uppercase font-semibold rounded-sm px-2 py-0.5 bg-warn/15 text-warn">
              draft
            </span>
          )}
        </div>
        <p className="text-muted max-w-3xl">{active.plain_summary}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CalloutList
          label="PII columns"
          items={active.pii_columns}
          tone="danger"
          empty="No PII detected."
        />
        <CalloutList
          label="Ambiguous columns"
          items={active.ambiguous_columns}
          tone="warn"
          empty="No ambiguities flagged."
        />
      </div>

      <ReviewerOverrideCallout
        columns={(active.column_inference ?? []).filter((c) => c.reviewer_overridden)}
      />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm uppercase tracking-wide text-muted m-0">
            json_schema
          </h3>
          <Button variant="ghost" onClick={exportSchema} title="Download the schema as a JSON file">
            Export schema
          </Button>
        </div>
        <JsonEditor
          label="json_schema"
          value={active.json_schema}
          onApply={(nextSchema) =>
            onDraftChange?.({
              ...active,
              json_schema: nextSchema as Record<string, unknown>,
            })
          }
          onReset={() => schema.json_schema}
          heightRem={22}
        />
      </div>

      {draft && (
        <div className="flex items-center gap-3 pt-2">
          <Button variant="primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save draft'}
          </Button>
          <Button variant="secondary" onClick={onDiscard} disabled={saving}>
            Discard changes
          </Button>
          {saveError && (
            <span role="alert" className="text-sm text-danger">
              {saveError}
            </span>
          )}
        </div>
      )}

      <ColumnPolicyOverrides
        columns={active.column_inference ?? []}
        initial={initialColumnPolicies}
        onSave={onSaveColumnPolicies}
      />
    </section>
  );
}

/**
 * v0.5 Stage E.4 / F.5 — per-column policy override input table.
 *
 * Three reviewer-tunable knobs:
 *
 * 1. **invalid_threshold** (E.4) — each cleanser has a hardcoded
 *    threshold (e.g. numeric: 0.10 ``UNCASTABLE_THRESHOLD``) for the
 *    ratio of unparseable rows above which it surfaces a column-level
 *    anomaly banner. Reviewers who know a column's data is naturally
 *    noisy can raise it; stricter reviewers lower it. The structural-
 *    blocking floor (``STRUCTURAL_THRESHOLD = 0.50``) is NOT
 *    adjustable — a 60%-unparseable column halts the pipeline
 *    regardless of this setting.
 *
 * 2. **text_language** (F.5) — ISO 639-1 language code for the
 *    text_normaliser's stopword + lemmatisation pipeline. Default
 *    ``en`` when blank. Lemmatisation is English-only — non-English
 *    columns get tokenisation + stopword removal but no lemmas.
 *
 * 3. **text_nlp_enabled** (F.5) — toggle for the NLP block
 *    (tokenise / stopwords / lemmatise). Default true. Set to
 *    "Disabled" for freetext columns where NLP adds noise (e.g.
 *    product codes misclassified as freetext).
 *
 * 4. **text_extra_stopwords** (v0.6 Stage I) — reviewer-added domain
 *    stopwords merged with the NLTK language default before stopword
 *    removal. Comma-separated text input; the server normalises
 *    (strip + lowercase + dedupe) before writing the audit row. Empty
 *    = no extension (NLTK list only). Use cases: stripping recurring
 *    boilerplate (``click``, ``submit``, ``page``) in UI-event-log
 *    columns where the analytics task isn't interested in those tokens.
 *
 * UI:
 * - Renders one row per column with four editor cells.
 * - Empty / "(default)" = no override (cleanser default applies).
 * - Save dispatches one PATCH per change (handled by the parent).
 */
interface ColumnPolicyOverridesProps {
  columns: ColumnInference[];
  initial?: Record<string, ColumnPolicyOverride>;
  onSave?: (
    overrides: Record<string, ColumnPolicyOverride>,
  ) => Promise<void> | void;
}

// Tri-state for the text_nlp_enabled column. ``""`` = no override
// (cleanser default applies); ``"true"`` / ``"false"`` = explicit
// reviewer pick. Encoded as strings so a single ``<select>`` can
// handle all three states without nullable-bool gymnastics in
// component state.
type NlpDraftValue = '' | 'true' | 'false';

/**
 * v0.6 Stage I — parse the reviewer's comma-separated stopwords
 * input into a canonical list. Strip each entry, lowercase, drop
 * empties, dedupe with stable order. Matches the server-side
 * normalisation in ``PolicyStore._cast_text_extra_stopwords`` and
 * ``runs.py`` PATCH handler so the dirty-check compares against
 * the same shape the server persisted.
 */
function parseStopwordTokens(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const piece of raw.split(',')) {
    const token = piece.trim().toLowerCase();
    if (!token || seen.has(token)) continue;
    seen.add(token);
    out.push(token);
  }
  return out;
}

function ColumnPolicyOverrides({
  columns,
  initial,
  onSave,
}: ColumnPolicyOverridesProps) {
  const initialMap = useMemo(() => initial ?? {}, [initial]);

  // Per-column draft text. Empty string = "no override" (cleanser
  // default applies). Numeric string = pending override; parsed at
  // save time. Initialised from ``initialMap``.
  const [drafts, setDrafts] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const [col, policy] of Object.entries(initialMap)) {
      if (policy.invalid_threshold != null) {
        out[col] = String(policy.invalid_threshold);
      }
    }
    return out;
  });
  // v0.5 Stage F.5 — per-column text_language draft. Empty string =
  // no override (cleanser uses ``en`` default).
  const [languageDrafts, setLanguageDrafts] = useState<Record<string, string>>(
    () => {
      const out: Record<string, string> = {};
      for (const [col, policy] of Object.entries(initialMap)) {
        if (policy.text_language != null) {
          out[col] = policy.text_language;
        }
      }
      return out;
    },
  );
  // v0.5 Stage F.5 — per-column text_nlp_enabled draft. Tri-state
  // (see ``NlpDraftValue``).
  const [nlpDrafts, setNlpDrafts] = useState<Record<string, NlpDraftValue>>(
    () => {
      const out: Record<string, NlpDraftValue> = {};
      for (const [col, policy] of Object.entries(initialMap)) {
        if (policy.text_nlp_enabled != null) {
          out[col] = policy.text_nlp_enabled ? 'true' : 'false';
        }
      }
      return out;
    },
  );
  // v0.6 Stage I — per-column text_extra_stopwords draft. Stored as
  // the comma-separated text the reviewer typed; the dirty-check
  // tokenises on save. Empty string = no override (cleanser falls
  // back to NLTK-only). Initialised from the persisted override
  // list joined by commas so the textbox shows the prior pick.
  const [stopwordsDrafts, setStopwordsDrafts] = useState<Record<string, string>>(
    () => {
      const out: Record<string, string> = {};
      for (const [col, policy] of Object.entries(initialMap)) {
        if (policy.text_extra_stopwords && policy.text_extra_stopwords.length > 0) {
          out[col] = policy.text_extra_stopwords.join(', ');
        }
      }
      return out;
    },
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  const dirty = useMemo(() => {
    const out: Record<string, ColumnPolicyOverride> = {};
    const colSet = new Set(columns.map((c) => c.column));

    function patch(col: string, fields: ColumnPolicyOverride): void {
      out[col] = { ...(out[col] ?? {}), ...fields };
    }

    for (const col of colSet) {
      const initialPolicy = initialMap[col];

      // --- invalid_threshold (E.4) ---
      const draftText = drafts[col] ?? '';
      const draftEmpty = draftText.trim() === '';
      const initialThreshold = initialPolicy?.invalid_threshold ?? null;

      if (draftEmpty) {
        if (initialThreshold != null) {
          patch(col, { invalid_threshold: null });
        }
      } else {
        const draftValue = Number(draftText);
        if (Number.isFinite(draftValue) && draftValue >= 0 && draftValue <= 1) {
          if (draftValue !== initialThreshold) {
            patch(col, { invalid_threshold: draftValue });
          }
        }
      }

      // --- text_language (F.5) ---
      const langText = (languageDrafts[col] ?? '').trim();
      const initialLang = initialPolicy?.text_language ?? null;
      if (langText === '') {
        if (initialLang != null) {
          patch(col, { text_language: null });
        }
      } else if (langText !== initialLang) {
        patch(col, { text_language: langText });
      }

      // --- text_nlp_enabled (F.5) ---
      const nlpDraft = nlpDrafts[col] ?? '';
      const initialNlp = initialPolicy?.text_nlp_enabled ?? null;
      const nlpDraftBool: boolean | null =
        nlpDraft === 'true' ? true : nlpDraft === 'false' ? false : null;
      if (nlpDraftBool === null) {
        if (initialNlp !== null) {
          patch(col, { text_nlp_enabled: null });
        }
      } else if (nlpDraftBool !== initialNlp) {
        patch(col, { text_nlp_enabled: nlpDraftBool });
      }

      // --- text_extra_stopwords (v0.6 Stage I) ---
      // Parse the textbox into canonical tokens (matches the
      // server's normalisation), then compare element-wise against
      // the persisted list. Order-sensitive: ``parseStopwordTokens``
      // emits stable insertion order, and the server stores the
      // same shape, so a comma reshuffle without content change
      // would still count as dirty — that's acceptable here; the
      // server-side normalisation re-canonicalises either way.
      const stopwordsRaw = stopwordsDrafts[col] ?? '';
      const stopwordsTokens = parseStopwordTokens(stopwordsRaw);
      const initialStopwords = initialPolicy?.text_extra_stopwords ?? null;
      if (stopwordsTokens.length === 0) {
        if (initialStopwords && initialStopwords.length > 0) {
          patch(col, { text_extra_stopwords: null });
        }
      } else if (
        initialStopwords === null ||
        initialStopwords === undefined ||
        initialStopwords.length !== stopwordsTokens.length ||
        initialStopwords.some((tok, i) => tok !== stopwordsTokens[i])
      ) {
        patch(col, { text_extra_stopwords: stopwordsTokens });
      }
    }
    return out;
  }, [columns, drafts, languageDrafts, nlpDrafts, stopwordsDrafts, initialMap]);

  const validationErrors = useMemo(() => {
    const errs: string[] = [];
    for (const [col, text] of Object.entries(drafts)) {
      const trimmed = text.trim();
      if (trimmed === '') continue;
      const v = Number(trimmed);
      if (!Number.isFinite(v) || v < 0 || v > 1) {
        errs.push(
          `${col}: invalid_threshold must be a number between 0.0 and 1.0`,
        );
      }
    }
    // v0.5 Stage F.5 — text_language length matches the schema
    // (2-5 chars covers ISO 639-1 + optional region tag).
    for (const [col, text] of Object.entries(languageDrafts)) {
      const trimmed = text.trim();
      if (trimmed === '') continue;
      if (trimmed.length < 2 || trimmed.length > 5) {
        errs.push(
          `${col}: text_language must be 2-5 chars (e.g. "en", "fr", "en-GB")`,
        );
      }
    }
    return errs;
  }, [drafts, languageDrafts]);

  if (!onSave) return null;
  if (columns.length === 0) return null;

  const dirtyCount = Object.keys(dirty).length;
  const submitDisabled = saving || validationErrors.length > 0 || dirtyCount === 0;

  async function handleSave() {
    if (submitDisabled) return;
    setSaving(true);
    setError(null);
    setSavedNote(null);
    try {
      await onSave!(dirty);
      setSavedNote(
        `Saved ${dirtyCount} column ${dirtyCount === 1 ? 'override' : 'overrides'}; pipeline re-running.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="space-y-3 pt-4 border-t border-border"
      data-testid="column-policy-overrides"
    >
      <div>
        <h3 className="text-sm uppercase tracking-wide text-muted m-0">
          Per-column policy overrides
        </h3>
        <p className="text-xs text-muted m-0 mt-1">
          Tune cleanser behaviour per column. ``invalid_threshold``
          adjusts how tolerant a cleanser is of unparseable rows
          before surfacing a banner (the 50% structural-blocking floor
          is fixed and not adjustable here). ``text_language`` selects
          the stopword + lemmatisation language for freetext columns;
          ``text_nlp`` toggles the NLP block on/off for freetext
          columns where NLP adds noise; ``extra_stopwords`` is a
          comma-separated list of domain stopwords merged with the
          language default. Leave blank / "(default)" to use the
          cleanser's default.
        </p>
      </div>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm" role="table">
          <thead className="bg-surface-alt/50 text-xs uppercase tracking-wide text-muted">
            <tr>
              <th scope="col" className="text-left px-3 py-2">column</th>
              <th scope="col" className="text-left px-3 py-2">type</th>
              <th scope="col" className="text-left px-3 py-2">
                invalid_threshold
              </th>
              <th scope="col" className="text-left px-3 py-2">
                text_language
              </th>
              <th scope="col" className="text-left px-3 py-2">
                text_nlp
              </th>
              <th scope="col" className="text-left px-3 py-2">
                extra_stopwords
              </th>
            </tr>
          </thead>
          <tbody>
            {columns.map((c) => {
              const draftText = drafts[c.column] ?? '';
              const langText = languageDrafts[c.column] ?? '';
              const nlpDraft = nlpDrafts[c.column] ?? '';
              const stopwordsText = stopwordsDrafts[c.column] ?? '';
              const isFreetext = c.inferred_type === 'freetext';
              return (
                <tr
                  key={c.column}
                  className="border-t border-border/50"
                  data-testid={`column-policy-row-${c.column}`}
                >
                  <td className="px-3 py-2 font-mono text-xs">{c.column}</td>
                  <td className="px-3 py-2 text-xs text-muted">
                    {c.inferred_type}
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={0}
                      max={1}
                      step={0.01}
                      value={draftText}
                      placeholder="(default)"
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [c.column]: e.target.value,
                        }))
                      }
                      disabled={saving}
                      className="w-28 rounded border border-border bg-paper px-2 py-1 text-xs font-mono"
                      data-testid={`column-policy-input-${c.column}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      maxLength={5}
                      value={langText}
                      placeholder={isFreetext ? '(en)' : '—'}
                      onChange={(e) =>
                        setLanguageDrafts((prev) => ({
                          ...prev,
                          [c.column]: e.target.value,
                        }))
                      }
                      disabled={saving || !isFreetext}
                      className="w-20 rounded border border-border bg-paper px-2 py-1 text-xs font-mono disabled:opacity-50"
                      data-testid={`column-policy-language-${c.column}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={nlpDraft}
                      onChange={(e) =>
                        setNlpDrafts((prev) => ({
                          ...prev,
                          [c.column]: e.target.value as NlpDraftValue,
                        }))
                      }
                      disabled={saving || !isFreetext}
                      className="w-28 rounded border border-border bg-paper px-2 py-1 text-xs disabled:opacity-50"
                      data-testid={`column-policy-nlp-${c.column}`}
                    >
                      <option value="">(default)</option>
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={stopwordsText}
                      placeholder={
                        isFreetext ? 'click, submit, page' : '—'
                      }
                      onChange={(e) =>
                        setStopwordsDrafts((prev) => ({
                          ...prev,
                          [c.column]: e.target.value,
                        }))
                      }
                      disabled={saving || !isFreetext}
                      className="w-56 rounded border border-border bg-paper px-2 py-1 text-xs font-mono disabled:opacity-50"
                      data-testid={`column-policy-stopwords-${c.column}`}
                      title="Comma-separated domain stopwords to strip alongside the language default. Server normalises (lowercase + dedupe) before save."
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {validationErrors.length > 0 && (
        <ul
          className="text-xs text-warn list-disc list-inside"
          data-testid="column-policy-validation"
        >
          {validationErrors.map((msg) => (
            <li key={msg}>{msg}</li>
          ))}
        </ul>
      )}
      {error && (
        <p className="text-sm text-danger" data-testid="column-policy-error">
          {error}
        </p>
      )}
      {savedNote && !error && (
        <p
          className="text-xs text-brand-dark"
          data-testid="column-policy-saved-note"
        >
          {savedNote}
        </p>
      )}
      <div className="flex items-center gap-3">
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={submitDisabled}
          data-testid="column-policy-save"
        >
          {saving
            ? 'Saving…'
            : dirtyCount === 0
              ? 'No changes'
              : `Save ${dirtyCount} column ${dirtyCount === 1 ? 'change' : 'changes'}`}
        </Button>
      </div>
    </div>
  );
}

function ReviewerOverrideCallout({ columns }: { columns: ColumnInference[] }) {
  if (columns.length === 0) return null;
  return (
    <div className="rounded-md border border-border p-4">
      <h3 className="text-sm uppercase tracking-wide text-muted mb-2">
        Reviewer-set columns
      </h3>
      <p className="text-xs text-muted mb-3">
        These types were chosen by a reviewer on a prior run and carried forward.
      </p>
      <ul className="flex flex-wrap gap-2">
        {columns.map((c) => (
          <li key={c.column}>
            <span className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs font-semibold bg-accent/10 text-accent">
              {c.column}
              <span className="text-[10px] font-normal opacity-75">
                → {c.overridden_type ?? c.inferred_type}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CalloutList({
  label,
  items,
  tone,
  empty,
}: {
  label: string;
  items: string[];
  tone: 'danger' | 'warn';
  empty: string;
}) {
  const toneCls = tone === 'danger' ? 'bg-danger/10 text-danger' : 'bg-warn/10 text-warn';
  return (
    <div className="rounded-md border border-border p-4">
      <h3 className="text-sm uppercase tracking-wide text-muted mb-2">{label}</h3>
      {items.length === 0 ? (
        <p className="text-sm text-muted">{empty}</p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {items.map((c) => (
            <li key={c}>
              <span className={`inline-block rounded-sm px-2 py-0.5 text-xs font-semibold ${toneCls}`}>
                {c}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
